import { gql, useMutation } from '@apollo/client'
import cx from 'classnames'
import KitIdInput from 'components/Dashboard/KitIdInput'
import { useToast } from 'components/Dashboard/Toast'
import { Modal2 } from 'components/Modal'
import SubmitButton from 'components/SubmitButton'
import { Form, Formik } from 'formik'
import rollbar from 'helpers/rollbar'
import { useState } from 'react'
import { resultHistoryFragment } from './ResultHistory'
import { AmendResultKitId, AmendResultKitIdVariables } from './__generated__/AmendResultKitId'

const amendResultKitIdMutation = gql`
  mutation AmendResultKitId($resultId: ID!, $kitId: ID!) {
    amendResultKitId(resultId: $resultId, kitId: $kitId) {
      success
      result {
        id
        status
        statedKit {
          id
          sku
          status
          registrationInfo {
            firstName
            lastName
            dob
          }
        }

        ...ResultHistoryFragment
      }
    }
  }

  ${resultHistoryFragment}
`

interface FormValues {
  kitId: string
}

interface AmendKitIdButtonProps {
  resultId: string
  initialKitId?: string
  children: React.ReactNode
  className?: string
}

const AmendKitIdButton = ({ resultId, initialKitId, children, className }: AmendKitIdButtonProps) => {
  const { addToast } = useToast()
  const [modalShown, setModalShown] = useState(false)

  const [amendResultKitId, { loading }] = useMutation<AmendResultKitId, AmendResultKitIdVariables>(
    amendResultKitIdMutation
  )

  return (
    <>
      <button
        type="button"
        className={cx(className, 'button is-small is-text', {
          'is-loading': loading,
        })}
        onClick={() => setModalShown(true)}
      >
        {children}
      </button>

      <Modal2 isOpen={modalShown} onRequestClose={() => setModalShown(false)} header="Amend kit ID for result">
        <Formik<FormValues>
          initialValues={{ kitId: initialKitId }}
          onSubmit={async (values) => {
            try {
              await amendResultKitId({
                variables: { resultId: resultId, kitId: values.kitId },
              })

              addToast({
                messageType: 'success',
                title: 'Amended kit ID',
                content: `Changed kit ID`,
                timeout: 3000,
              })
              setModalShown(false)
            } catch (err) {
              rollbar.error('Error changing kit ID', err)
              addToast({
                messageType: 'error',
                title: 'Error',
                content: `Failed to change kit ID: ${err.message}`,
              })
            }
          }}
        >
          {({ isSubmitting, status, values }) => (
            <Form>
              <KitIdInput name="kitId" />

              <div className="buttons is-right mt-5">
                <SubmitButton className="is-primary" submitting={isSubmitting}>
                  Set kit ID to {values.kitId}
                </SubmitButton>
              </div>
              {status?.error && (
                <article className="message is-danger">
                  <div className="message-body">Failed: {status.error}</div>
                </article>
              )}
            </Form>
          )}
        </Formik>
      </Modal2>
    </>
  )
}

export default AmendKitIdButton
